import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import LOGO from "../../../assets/img/MOSIDA-logo.png";
import FifaBg from "../../../assets/img/fifa/fifa-inner.png";
import LoaderSaveModal from "../../../modal/LoaderSaveModal";
import SigninModal from "../../../modal/SigninModal";
import SignupModal from "../../../modal/SignupModal";
import ForgotPasswordModal from "../../../modal/ForgotPasswordModal";
import VerificationModal from "../../../modal/VerificationModal";
import ResetModal from "../../../modal/ResetModal";
import SuccessModal from "../../../modal/SuccessModal";
import EmailVerificationModal from "../../../modal/EmailVerificationModal";
import { authService } from "../../../modal/auth/authService";
import type { AuthUser } from "../../../modal/auth/types";
import { createFavourite, userAlreadyHasFavouriteImage } from "../../../services/favouritesService";
import { resolveSelectedAiSelfieArtFavouriteType } from "../../../services/stylePromptResolver";
import type { RootState } from "../../../redux/store";
import { resolveFifaGeneratedCardPreview } from "../../../utils/fifaCardPreview";
import FifaCardPreviewWithShimmer from "../../../compoment/features/FifaCardPreviewWithShimmer";
import {
  isFifaWorldCup2026Station,
  persistFifaCardSaveRequested,
  persistFifaCardTryAgainUsed,
  persistFifaGeneratedCard,
  readActiveStoredStation,
  readFifaCardTryAgainUsed,
  readFifaGeneratedCard,
  readFifaPlayerProfile,
  readFifaPlayerStats,
  readSelectedFifaCardDesign,
  resolveFifaCapturedImage,
  resolveFifaStationSlug,
  resolveStationRouteSlug,
  type FifaPlayerProfile,
  type FifaPlayerStats,
} from "../../../utils/stationFlowStorage";
import { readSelectedAiSelfieArtFromStorage } from "../../../services/stylePromptResolver";
import { finalizeOrderWithGeneratedArt } from "../../../utils/finalizeOrderWithGeneratedArt";
import { clearPendingPaymentOrder } from "../../../utils/pendingPaymentOrder";
import { resolveAuthAccessToken } from "../../../utils/authSession";

const CLAIM_NAVIGATE_MS = 4500;
const MIN_LOADER_MS = 2500;

type SaveFifaCardPhase = "save-prompt" | "claim-instructions";

const SaveFifaCard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const uploadedImageUrl = useSelector(
    (state: RootState) => state.uploadFlow.uploadedImageUrl
  );

  const [phase, setPhase] = useState<SaveFifaCardPhase>("save-prompt");
  const [loading, setLoading] = useState(false);
  const [signinModal, setSigninModal] = useState(false);
  const [signupModal, setSignupModal] = useState(false);
  const [emailVerificationModal, setEmailVerificationModal] = useState(false);
  const [forgotPasswordModal, setForgotPasswordModal] = useState(false);
  const [verificationModal, setVerificationModal] = useState(false);
  const [resetModal, setResetModal] = useState(false);
  const [successModal, setSuccessModal] = useState(false);
  const [isUpdatingOrder, setIsUpdatingOrder] = useState(false);

  const stationData = useMemo(() => readActiveStoredStation(), [location.state]);
  const selectedAiSelfieArt = useMemo(
    () => readSelectedAiSelfieArtFromStorage(),
    [location.state]
  );
  const stationSlug = useMemo(
    () => resolveFifaStationSlug(stationData, selectedAiSelfieArt),
    [stationData, selectedAiSelfieArt]
  );
  const isFifaFlow = isFifaWorldCup2026Station(stationData, selectedAiSelfieArt);

  const locationState = location.state as {
    capturedImage?: string;
    stationData?: typeof stationData;
    fifaPlayerProfile?: FifaPlayerProfile;
    fifaPlayerStats?: FifaPlayerStats;
    fifaGeneratedCard?: string;
    fifaStatsSource?: "manual" | "ai";
    fifaStatsSkipped?: boolean;
    fifaCompareComplete?: boolean;
  } | null;

  const capturedImage = resolveFifaCapturedImage({
    locationImage: locationState?.capturedImage,
    reduxImage: uploadedImageUrl,
  });
  const fifaPlayerProfile =
    locationState?.fifaPlayerProfile || readFifaPlayerProfile();
  const fifaPlayerStats = locationState?.fifaPlayerStats || readFifaPlayerStats();
  const cardImageUrl =
    locationState?.fifaGeneratedCard ||
    readFifaGeneratedCard() ||
    resolveFifaGeneratedCardPreview(readSelectedFifaCardDesign());

  const showTryOneMore =
    !readFifaCardTryAgainUsed() && !locationState?.fifaCompareComplete;

  const retryRouteState = {
    capturedImage,
    stationData,
    fifaPlayerProfile,
    fifaPlayerStats,
    fifaStatsSource: locationState?.fifaStatsSource,
    fifaStatsSkipped: locationState?.fifaStatsSkipped,
    fifaRetryGeneration: true,
    previousFifaGeneratedCard: cardImageUrl,
  };

  useEffect(() => {
    if (cardImageUrl) {
      persistFifaGeneratedCard(cardImageUrl);
    }
  }, [cardImageUrl]);

  useEffect(() => {
    if (!isFifaFlow) {
      navigate(`/${stationSlug}/choose-option`, { replace: true });
      return;
    }

    if (!capturedImage || !fifaPlayerProfile) {
      navigate(`/${stationSlug}/upload-fifa-image`, { replace: true });
    }
  }, [capturedImage, fifaPlayerProfile, isFifaFlow, navigate, stationSlug]);

  useEffect(() => {
    if (phase !== "claim-instructions") return;

    const timer = window.setTimeout(() => {
      clearPendingPaymentOrder();
      const artSlug = resolveStationRouteSlug(stationData, undefined, selectedAiSelfieArt);
      navigate(`/${artSlug}/print-frame-selection`, {
        state: {
          stationData,
          snapshot: cardImageUrl,
          fifaWorldCupCard: true,
        },
      });
    }, CLAIM_NAVIGATE_MS);

    return () => window.clearTimeout(timer);
  }, [cardImageUrl, navigate, phase, selectedAiSelfieArt, stationData]);

  const navigateToPrint = () => {
    clearPendingPaymentOrder();
    const artSlug = resolveStationRouteSlug(stationData, undefined, selectedAiSelfieArt);
    navigate(`/${artSlug}/print-frame-selection`, {
      state: {
        stationData,
        snapshot: cardImageUrl,
        fifaWorldCupCard: true,
      },
    });
  };

  const attachCardToPaidOrder = async (): Promise<boolean> => {
    const trimmed = cardImageUrl?.trim();
    if (!trimmed) return true;

    setIsUpdatingOrder(true);
    try {
      const result = await finalizeOrderWithGeneratedArt(trimmed, { retainOrder: true });
      if (!result.success && !result.skipped) {
        window.alert(
          result.error || "Failed to attach your artwork to the order. Please try again."
        );
        return false;
      }
      return true;
    } finally {
      setIsUpdatingOrder(false);
    }
  };

  const handleNo = async () => {
    const attached = await attachCardToPaidOrder();
    if (!attached) return;

    persistFifaCardSaveRequested(false);
    if (cardImageUrl) {
      try {
        localStorage.setItem("favouriteImg", cardImageUrl);
      } catch {
        /* ignore storage errors */
      }
    }
    setPhase("claim-instructions");
  };

  const handleYes = async () => {
    const attached = await attachCardToPaidOrder();
    if (!attached) return;

    setSigninModal(true);
  };

  const handleAuthSuccess = async (user: AuthUser) => {
    persistFifaCardSaveRequested(true);

    if (cardImageUrl) {
      try {
        localStorage.setItem("favouriteImg", cardImageUrl);
      } catch {
        /* ignore storage errors */
      }
    }

    setSigninModal(false);
    setSignupModal(false);
    setEmailVerificationModal(false);
    setForgotPasswordModal(false);
    setVerificationModal(false);
    setResetModal(false);
    setSuccessModal(false);

    setLoading(true);
    const minimumDelay = new Promise<void>((resolve) =>
      window.setTimeout(resolve, MIN_LOADER_MS)
    );

    try {
      const favouriteType = resolveSelectedAiSelfieArtFavouriteType();
      const alreadySaved = await userAlreadyHasFavouriteImage(
        user.id,
        cardImageUrl,
        resolveAuthAccessToken(user)
      );
      await Promise.all([
        minimumDelay,
        alreadySaved
          ? Promise.resolve()
          : createFavourite({
              userId: user.id,
              imageUrl: cardImageUrl || undefined,
              favouriteType,
              type: favouriteType,
            }),
      ]);
    } finally {
      setLoading(false);
      navigateToPrint();
    }
  };

  const handleGoogleAuth = async () => {
    await authService.googleAuth();
  };

  const handleTryOneMore = () => {
    persistFifaCardTryAgainUsed(true);
    navigate(`/${stationSlug}/creating-fifa-card`, {
      state: retryRouteState,
    });
  };

  if (!isFifaFlow || !capturedImage || !fifaPlayerProfile) {
    return null;
  }

  return (
    <>
      <section
        className="saveFifaCardSection"
        style={{ backgroundImage: `url(${FifaBg})` }}
      >
        <div className="saveFifaCardInner">
          <div className="mosidaLogoWhite saveFifaCardLogo mb-5">
            <img src={LOGO} alt="MOSIDA logo" className="logo" />
            <span className="badgeCreative">FIFA World Cup</span>
          </div>

          <div className="saveFifaCardBody">
            <FifaCardPreviewWithShimmer
              imageUrl={cardImageUrl}
              alt="Your FIFA World Cup card"
              variant="save"
            />

            {phase === "save-prompt" ? (
              <>
                <h1 className="saveFifaCardHeading">
                  <div className="saveFifaCardHeadingLine">Would you like to save your <span className="saveFifaCardHeadingLine--gold">
                    World Cup Card?
                  </span></div>
                </h1>

                <div className="saveFifaCardActions">
                  <button
                    type="button"
                    className="saveFifaCardBtn saveFifaCardBtn--yes"
                    onClick={handleYes}
                    disabled={isUpdatingOrder}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    className="saveFifaCardBtn saveFifaCardBtn--no"
                    onClick={handleNo}
                    disabled={isUpdatingOrder}
                  >
                    No
                  </button>
                </div>

                {/* {showTryOneMore && (
                  <button
                    type="button"
                    className="saveFifaCardTryOneMore mt-5"
                    onClick={handleTryOneMore}
                  >
                    Generate one more
                  </button>
                )} */}
              </>
            ) : (
              <h1 className="saveFifaCardHeading">
                <div className="saveFifaCardHeadingLine">
                  You can claim your <span className="saveFifaCardHeadingLine--gold">
                    World Cup Card {" "}
                  </span>
                  at the printing station.
                </div>
              </h1>
            )}
          </div>
        </div>
      </section>

      {signinModal && (
        <SigninModal
          show={signinModal}
          onHide={() => setSigninModal(false)}
          setSignupModal={setSignupModal}
          setForgotPasswordModal={setForgotPasswordModal}
          setSigninModal={setSigninModal}
          onAuthSuccess={handleAuthSuccess}
          onGoogleAuth={handleGoogleAuth}
        />
      )}
      {forgotPasswordModal && (
        <ForgotPasswordModal
          show={forgotPasswordModal}
          onHide={() => setForgotPasswordModal(false)}
          setForgotPasswordModal={setForgotPasswordModal}
          setSigninModal={setSigninModal}
          setSignupModal={setSignupModal}
          setVerificationModal={setVerificationModal}
        />
      )}
      {verificationModal && (
        <VerificationModal
          show={verificationModal}
          onHide={() => setVerificationModal(false)}
          setResetModal={setResetModal}
          setVerificationModal={setVerificationModal}
        />
      )}
      {resetModal && (
        <ResetModal
          show={resetModal}
          onHide={() => setResetModal(false)}
          setResetModal={setResetModal}
          setSuccessModal={setSuccessModal}
        />
      )}
      {successModal && (
        <SuccessModal
          show={successModal}
          onHide={() => setSuccessModal(false)}
          setSuccessModal={setSuccessModal}
          setSigninModal={setSigninModal}
        />
      )}
      {signupModal && (
        <SignupModal
          show={signupModal}
          onHide={() => setSignupModal(false)}
          setSignupModal={setSignupModal}
          setSigninModal={setSigninModal}
          setEmailVerificationModal={setEmailVerificationModal}
          onAuthSuccess={handleAuthSuccess}
          onGoogleAuth={handleGoogleAuth}
        />
      )}
      {emailVerificationModal && (
        <EmailVerificationModal
          show={emailVerificationModal}
          onHide={() => setSignupModal(false)}
          setSaveModal={() => undefined}
        />
      )}
      {loading && (
        <LoaderSaveModal
          show={loading}
          loading={loading}
          success={false}
          onHide={() => setLoading(false)}
        />
      )}
    </>
  );
};

export default SaveFifaCard;
